/**
 * Presentation-detail KPI table. Mirrors the legacy
 * `wf-audit/.../presenationItem` right-column header block:
 *
 *   ┌──────────┬─────────────┬───────┬─────────────────────────┬─────────────────────┐
 *   │ Scenario │ Hours Saved │ Value │ Successful Executions % │ Operations %        │
 *   ├──────────┼─────────────┼───────┼─────────────────────────┼─────────────────────┤
 *   │ <name>   │  bar+number │  $X   │  bar+number             │  bar+number         │
 *   └──────────┴─────────────┴───────┴─────────────────────────┴─────────────────────┘
 *
 * Per-cell `bar-tint` visualisation matches the legacy:
 *   - the cell background renders as a filled progress bar (left-aligned)
 *     in the column's accent color
 *   - the percentage / number sits over the bar
 *
 * Column accents (legacy parity):
 *   - Hours Saved        : violet (matches the Cost-Saving card)
 *   - Value              : brown / orange
 *   - Successful Exec %  : green
 *   - Operations %       : blue
 *
 * The legacy table is driven by per-scenario KPI rollups. Today the
 * engine doesn't expose a `/v1/kpi-values` aggregate yet, so the
 * caller passes `data: null` and we render the 4-column header + a
 * single "No KPI data available" empty row. The visual layout slots
 * in correctly so swapping to live data later is a one-prop change.
 */
export interface PresentationKpiRow {
  scenarioName: string;
  hoursSaved: number;
  /** USD; rendered with a leading `$` and locale formatting. */
  value: number;
  /** 0–100; pct shown to operator. */
  successfulExecutionsPct: number;
  /** 0–100. */
  operationsPct: number;
}

export interface PresentationKpiTableProps {
  rows: ReadonlyArray<PresentationKpiRow> | null;
}

const COL_ACCENT = {
  hours: 'rgba(168, 85, 247, 0.18)', // violet
  value: 'rgba(180, 83, 9, 0.18)', // brown / orange
  successful: 'rgba(34, 197, 94, 0.18)', // green
  operations: 'rgba(59, 130, 246, 0.18)', // blue
} as const;

export function PresentationKpiTable({ rows }: PresentationKpiTableProps): JSX.Element {
  const hasData = rows !== null && rows.length > 0;
  return (
    <div className="overflow-hidden rounded-lg border border-foreground/10 bg-foreground/[0.02]">
      <table className="w-full table-fixed text-sm">
        <thead className="text-xs uppercase tracking-wider text-foreground/65">
          <tr className="border-b border-foreground/10">
            <th className="w-[40%] px-3 py-2 text-left font-medium">Scenario</th>
            <Th tint={COL_ACCENT.hours}>Hours Saved</Th>
            <Th tint={COL_ACCENT.value}>Value</Th>
            <Th tint={COL_ACCENT.successful}>Successful Executions (%)</Th>
            <Th tint={COL_ACCENT.operations}>Operations (%)</Th>
          </tr>
        </thead>
        <tbody>
          {hasData ? (
            rows.map((r, i) => (
              <tr key={i} className="border-b border-foreground/5 last:border-b-0">
                <td className="truncate px-3 py-2 text-foreground" title={r.scenarioName}>
                  {r.scenarioName}
                </td>
                <BarCell
                  pct={Math.min(
                    100,
                    (r.hoursSaved /
                      Math.max(
                        1,
                        maxOf(rows, (x) => x.hoursSaved),
                      )) *
                      100,
                  )}
                  label={r.hoursSaved.toLocaleString()}
                  tint="rgb(168, 85, 247)"
                />
                <BarCell
                  pct={Math.min(
                    100,
                    (r.value /
                      Math.max(
                        1,
                        maxOf(rows, (x) => x.value),
                      )) *
                      100,
                  )}
                  label={`$${r.value.toLocaleString()}`}
                  tint="rgb(180, 83, 9)"
                />
                <BarCell
                  pct={r.successfulExecutionsPct}
                  label={`${r.successfulExecutionsPct.toFixed(0)}%`}
                  tint="rgb(34, 197, 94)"
                />
                <BarCell
                  pct={r.operationsPct}
                  label={`${r.operationsPct.toFixed(0)}%`}
                  tint="rgb(59, 130, 246)"
                />
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="px-3 py-6 text-center text-sm text-foreground/50">
                No KPI data available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, tint }: { children: React.ReactNode; tint: string }): JSX.Element {
  return (
    <th
      className="px-2 py-2 text-center font-medium text-foreground"
      style={{ backgroundColor: tint }}
    >
      {children}
    </th>
  );
}

/**
 * Cell rendered as a tinted progress bar with the value centered on top.
 * `pct` is clamped to 0–100. The bar fills left-aligned at `tint`
 * opacity; the label text colour stays neutral so it reads against
 * any fill width.
 */
function BarCell({ pct, label, tint }: { pct: number; label: string; tint: string }): JSX.Element {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <td className="relative px-2 py-2 text-center text-foreground">
      <div className="absolute inset-0">
        <div
          className="h-full"
          style={{ width: `${clamped}%`, backgroundColor: tint, opacity: 0.25 }}
        />
      </div>
      <span className="relative font-mono">{label}</span>
    </td>
  );
}

function maxOf<T>(rows: ReadonlyArray<T>, pick: (r: T) => number): number {
  let m = 0;
  for (const r of rows) {
    const v = pick(r);
    if (v > m) m = v;
  }
  return m;
}
